import type { ReactNode } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { ComposerPreset } from "../CreatePost/CreatePostForm";
import { SocialPostType } from "../../types/post.types";
import { getPostTypeColor, SOCIAL_POST_TYPE_VISUAL } from "../../utils/postType";

type PublishTypeCardProps = {
    title: string;
    description?: string;
    icon: ReactNode;
    color: string;
    selected?: boolean;
    compact?: boolean;
    onClick: () => void;
};

const PublishTypeCard = ({
    title,
    description,
    icon,
    color,
    selected = false,
    compact = false,
    onClick,
}: PublishTypeCardProps) => {
    const theme = useTheme();

    if (compact) {
        return (
            <Stack
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                alignItems="center"
                justifyContent="center"
                gap={0.75}
                onClick={onClick}
                sx={{
                    position: "relative",
                    width: "100%",
                    minHeight: 72,
                    p: 1.25,
                    border: `1px solid ${selected ? color : `${color}40`}`,
                    borderRadius: 2,
                    backgroundColor: selected ? `${color}14` : theme.palette.common.white,
                    boxShadow: selected ? "none" : "0px 2px 8px 0px rgba(42, 43, 39, 0.04)",
                    cursor: "pointer",
                    textAlign: "center",
                    font: "inherit",
                    color: selected ? color : theme.palette.text.primary,
                    transition: "background-color 150ms ease, border-color 150ms ease",
                    "&:hover": {
                        backgroundColor: `${color}14`,
                        borderColor: color,
                    },
                    "&:focus-visible": {
                        outline: `2px solid ${color}`,
                        outlineOffset: 2,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: `${color}20`,
                        color,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="small-bold" sx={{ lineHeight: 1.3, px: 0.5 }}>
                    {title}
                </Typography>
                {selected && (
                    <Box sx={{ position: "absolute", top: 6, right: 6, color }}>
                        <FoodsIcon icon="checkCircleFilled" size={1.5} />
                    </Box>
                )}
            </Stack>
        );
    }

    return (
        <Stack
            component="button"
            type="button"
            direction="row"
            alignItems="flex-start"
            gap={1.5}
            onClick={onClick}
            sx={{
                width: "100%",
                p: 1.5,
                border: `1px solid ${color}40`,
                borderRadius: 2,
                backgroundColor: theme.palette.common.white,
                boxShadow: "0px 2px 8px 0px rgba(42, 43, 39, 0.04)",
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                color: theme.palette.text.primary,
                transition: "background-color 150ms ease, border-color 150ms ease",
                "&:hover": {
                    backgroundColor: `${color}14`,
                    borderColor: color,
                },
                "&:focus-visible": {
                    outline: `2px solid ${color}`,
                    outlineOffset: 2,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: `${color}20`,
                    color,
                }}
            >
                {icon}
            </Box>
            <Stack flex={1} minWidth={0} gap={0.25}>
                <Typography variant="body-semibold" sx={{ lineHeight: 1.35, color: selected ? color : undefined }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="small" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {description}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

type PublishAction = {
    postType: Exclude<SocialPostType, "generic">;
    labelKey: string;
    defaultLabel: string;
    descriptionKey: string;
    defaultDescription: string;
    preset: ComposerPreset;
};

type SocialPublishActionsProps = {
    onActionClick: (preset: ComposerPreset) => void;
};

export const SocialPublishActions = ({ onActionClick }: SocialPublishActionsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const actions: PublishAction[] = [
        {
            postType: "question",
            labelKey: "social.publish_actions.ask_question",
            defaultLabel: "Ask a question",
            descriptionKey: "social.publish_actions.ask_question_hint",
            defaultDescription: "Get answers from the community",
            preset: { type: "question" },
        },
        {
            postType: "best_practice",
            labelKey: "social.publish_actions.share_best_practice",
            defaultLabel: "Share a best practice",
            descriptionKey: "social.publish_actions.share_best_practice_hint",
            defaultDescription: "Inspire others with what works",
            preset: { type: "best_practice" },
        },
        {
            postType: "experience",
            labelKey: "social.publish_actions.share_experience",
            defaultLabel: "Share feedback",
            descriptionKey: "social.publish_actions.share_experience_hint",
            defaultDescription: "Tell us what you learned",
            preset: { type: "experience" },
        },
    ];

    return (
        <Stack
            gap={1}
            role="group"
            aria-label={t("social.publish_actions.group_label", "Start a post")}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                {t("social.publish_actions.section_title", "Publication type:")}
            </Typography>
            <Stack gap={1}>
                {actions.map((action) => {
                    const visual = SOCIAL_POST_TYPE_VISUAL[action.postType];
                    const color = getPostTypeColor(theme, visual.colorToken);

                    return (
                        <PublishTypeCard
                            key={action.labelKey}
                            title={t(action.labelKey, action.defaultLabel)}
                            description={t(action.descriptionKey, action.defaultDescription)}
                            icon={<FoodsIcon icon={visual.icon} size={2} />}
                            color={color}
                            onClick={() => onActionClick(action.preset)}
                        />
                    );
                })}
            </Stack>
        </Stack>
    );
};

type ExpandedPostTypeSelectorProps = {
    value: string;
    onChange: (value: string) => void;
};

const EXPANDED_TYPE_LABEL_KEYS: Record<SocialPostType, string> = {
    generic: "social.create_post.types.generic",
    question: "social.create_post.types.question",
    best_practice: "social.create_post.types.best_practice",
    experience: "social.create_post.types.experience",
};

const EXPANDED_TYPE_ICONS: Record<SocialPostType, (typeof SOCIAL_POST_TYPE_VISUAL)[SocialPostType]["icon"]> = {
    generic: "tag",
    question: "questionMark",
    best_practice: "suggestions",
    experience: "cheer",
};

const EXPANDED_POST_TYPES = [
    "generic",
    "question",
    "best_practice",
    "experience",
] as const satisfies readonly SocialPostType[];

export const ExpandedPostTypeSelector = ({ value, onChange }: ExpandedPostTypeSelectorProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const groupLabel = t("social.create_post.types.group_label", "Post type");

    return (
        <Stack gap={0.75} role="radiogroup" aria-label={groupLabel}>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
                {groupLabel}
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
                    gap: 1,
                }}
            >
                {EXPANDED_POST_TYPES.map((postType) => {
                    const visual = SOCIAL_POST_TYPE_VISUAL[postType];
                    const color = getPostTypeColor(theme, visual.colorToken);
                    const selected = value === postType;

                    return (
                        <PublishTypeCard
                            key={postType}
                            compact
                            selected={selected}
                            title={t(EXPANDED_TYPE_LABEL_KEYS[postType], visual.defaultLabel)}
                            icon={<FoodsIcon icon={EXPANDED_TYPE_ICONS[postType]} size={1.75} />}
                            color={color}
                            onClick={() => onChange(postType)}
                        />
                    );
                })}
            </Box>
        </Stack>
    );
};
